import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchApiGet, fetchApiPost, fetchApiPut } from "./utils";
import type {
  ArchiveOtcInvoicesRequest,
  DownloadSentOtcInvoiceRequest,
  SendOtcInvoiceQueueEntriesRequest,
  SkipOtcInvoiceQueueEntriesRequest,
  UpdateOtcInvoiceAllocationsRequest,
} from "@/lib/validation/otcInvoices";

export const otcInvoiceQueueBaseKey = "otcInvoiceQueue";
export const otcInvoicesBaseKey = "otcInvoices";

export type OtcInvoiceQueueStatus = "queued" | "sent" | "skipped";
export type OtcInvoiceStatus = "active" | "archived";

export type PaginatedResponse<TItem> = {
  items: TItem[];
  page: number;
  page_size: number;
  has_next_page: boolean;
};

export type OtcInvoiceEligibilityReasonCode =
  | "description_not_implemented"
  | "customer_address_unavailable"
  | "invalid_queue_status"
  | "invalid_contract_number"
  | "invalid_commodity"
  | "invalid_invoice_quantity"
  | "invalid_option_premium"
  | "invalid_contract_fee"
  | "no_active_allocations"
  | "invalid_allocation"
  | "duplicate_allocation_customer"
  | "invalid_allocation_total"
  | "missing_customer"
  | "zero_price_queue_inconsistency"
  | "malformed_layer_state";

export type OtcInvoiceSendBlockingReason = {
  code: OtcInvoiceEligibilityReasonCode;
  message: string;
  data?: Record<string, unknown>;
};

export type OtcInvoiceQueueAllocationProjection = {
  id: string;
  customer_id: string;
  percentage: string;
  invoice_quantity: string | null;
  invoice_amount: string | null;
  Customer: {
    long_name: string;
  };
};

export type OtcInvoiceQueueListItem = {
  queue_entry_id: string;
  created_at: string;
  layer_id: string;
  status: OtcInvoiceQueueStatus;
  source_type: "layer";
  layer_no: number;
  amendment_type: string | null;
  contract_number: string | null;
  commodity: string;
  description: string | null;
  invoice_quantity: string | null;
  option_premium: string | null;
  contract_fee: string | null;
  allocations: OtcInvoiceQueueAllocationProjection[];
  can_send: boolean;
  send_blocking_reasons: OtcInvoiceSendBlockingReason[];
};

export type OtcInvoiceDto = {
  id: string;
  created_at: string;
  updated_at: string;
  otc_invoice_queue_entry_id: string;
  otc_invoice_queue_allocation_id: string;
  trading_contract_layer_id: string;
  customer_id: string;
  source_type: "queue";
  status: OtcInvoiceStatus;
  invoice_date: string;
  invoice_number: string;
  sent_at: string;
  sent_by_user_id: string;
  description: string;
  contract_number: string;
  layer_no: number;
  amendment_type: string | null;
  commodity: string;
  allocation_percentage: string;
  invoice_quantity: string;
  option_premium: string;
  contract_fee: string;
  invoice_amount: string;
  customer_name: string;
  address_line_one: string;
  address_line_two: string | null;
  city: string;
  state: string;
  postal_code: string;
  meta_data: Record<string, unknown>;
  archived_at: string | null;
  archived_by_user_id: string | null;
  archive_reason: string | null;
};

export type ListOtcInvoiceQueueQuery = {
  statuses: OtcInvoiceQueueStatus[];
  customer_id?: string;
  contract_number?: string;
  created_at_from?: string;
  created_at_to?: string;
  page: number;
  page_size: number;
};

export type ListOtcInvoicesQuery = {
  statuses: OtcInvoiceStatus[];
  customer_id?: string;
  invoice_date_from?: string;
  invoice_date_to?: string;
  invoice_number?: string;
  contract_number?: string;
  page: number;
  page_size: number;
};

export type BlockedOtcInvoiceQueueEntry = {
  queue_entry_id: string;
  contract_number: string | null;
  amendment_type: string | null;
  customer_names: string[];
  reasons: OtcInvoiceSendBlockingReason[];
};

export type SendOtcInvoiceQueueEntriesResult = {
  sent_at: string | null;
  invoice_date: string | null;
  invoice_number: string | null;
  sent_queue_entry_ids: string[];
  invoice_ids: string[];
  blocked: BlockedOtcInvoiceQueueEntry[];
  sent_count: number;
  blocked_count: number;
};

export type SkipOtcInvoiceQueueEntriesResult = {
  skipped_queue_entry_ids: string[];
  skipped_count: number;
  skipped_at: string;
};

export type ArchiveOtcInvoicesResult = {
  archived_invoice_ids: string[];
  archived_count: number;
  archived_at: string;
};

export type DownloadSentOtcInvoiceResult = {
  invoice_id: string;
  document_id: string;
  filename: string;
  presigned_get_url: string;
};

export type OtcInvoiceQueueAllocationDto = {
  id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  otc_invoice_queue_entry_id: string;
  customer_id: string;
  percentage: string;
  created_by_user_id: string | null;
  updated_by_user_id: string | null;
};

export type UpdateOtcInvoiceAllocationsResult = {
  allocations: OtcInvoiceQueueAllocationDto[];
  created_count: number;
  updated_count: number;
  deleted_count: number;
};

function appendArray(queryParams: URLSearchParams, key: string, values: string[]): void {
  if (values.length > 0) queryParams.set(key, values.join(","));
}

function queueQueryParams(query: ListOtcInvoiceQueueQuery): URLSearchParams {
  const queryParams = new URLSearchParams();
  appendArray(queryParams, "statuses", query.statuses);
  if (query.customer_id) queryParams.set("customer_id", query.customer_id);
  if (query.contract_number) queryParams.set("contract_number", query.contract_number);
  if (query.created_at_from) queryParams.set("created_at_from", query.created_at_from);
  if (query.created_at_to) queryParams.set("created_at_to", query.created_at_to);
  queryParams.set("page", query.page.toString());
  queryParams.set("page_size", query.page_size.toString());
  return queryParams;
}

function invoiceQueryParams(query: ListOtcInvoicesQuery): URLSearchParams {
  const queryParams = new URLSearchParams();
  appendArray(queryParams, "statuses", query.statuses);
  if (query.customer_id) queryParams.set("customer_id", query.customer_id);
  if (query.invoice_date_from) queryParams.set("invoice_date_from", query.invoice_date_from);
  if (query.invoice_date_to) queryParams.set("invoice_date_to", query.invoice_date_to);
  if (query.invoice_number) queryParams.set("invoice_number", query.invoice_number);
  if (query.contract_number) queryParams.set("contract_number", query.contract_number);
  queryParams.set("page", query.page.toString());
  queryParams.set("page_size", query.page_size.toString());
  return queryParams;
}

export function useListOtcInvoiceQueue(query: ListOtcInvoiceQueueQuery) {
  const queryParams = queueQueryParams(query);
  return useQuery({
    queryKey: [otcInvoiceQueueBaseKey, queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResponse<OtcInvoiceQueueListItem>>("otc/invoices/queue", queryParams),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useListOtcInvoices(query: ListOtcInvoicesQuery) {
  const queryParams = invoiceQueryParams(query);
  return useQuery({
    queryKey: [otcInvoicesBaseKey, queryParams.toString()],
    queryFn: () => fetchApiGet<PaginatedResponse<OtcInvoiceDto>>("otc/invoices", queryParams),
    keepPreviousData: true,
    retry: 1,
  });
}

export function useSendOtcInvoiceQueueEntries() {
  const queryClient = useQueryClient();
  return async (data: SendOtcInvoiceQueueEntriesRequest) => {
    const result = await fetchApiPost<SendOtcInvoiceQueueEntriesResult>("otc/invoices/queue/send", data);
    if (result.success) {
      queryClient.invalidateQueries({ queryKey: [otcInvoiceQueueBaseKey] });
      if (result.data.invoice_ids.length > 0) {
        queryClient.invalidateQueries({ queryKey: [otcInvoicesBaseKey] });
      }
    }
    return result;
  };
}

export function useSkipOtcInvoiceQueueEntries() {
  const queryClient = useQueryClient();
  return async (data: SkipOtcInvoiceQueueEntriesRequest) => {
    const result = await fetchApiPost<SkipOtcInvoiceQueueEntriesResult>("otc/invoices/queue/skip", data);
    if (result.success) queryClient.invalidateQueries({ queryKey: [otcInvoiceQueueBaseKey] });
    return result;
  };
}

export function useUpdateOtcInvoiceAllocations() {
  const queryClient = useQueryClient();
  return async (data: UpdateOtcInvoiceAllocationsRequest) => {
    const result = await fetchApiPut<UpdateOtcInvoiceAllocationsResult>("otc/invoices/queue/allocations", data);
    if (result.success) queryClient.invalidateQueries({ queryKey: [otcInvoiceQueueBaseKey] });
    return result;
  };
}

export function downloadSentOtcInvoice(data: DownloadSentOtcInvoiceRequest) {
  return fetchApiPost<DownloadSentOtcInvoiceResult>("otc/invoices/sent/download", data);
}

export function useArchiveOtcInvoices() {
  const queryClient = useQueryClient();
  return async (data: ArchiveOtcInvoicesRequest) => {
    const result = await fetchApiPost<ArchiveOtcInvoicesResult>("otc/invoices/archive", data);
    if (result.success) queryClient.invalidateQueries({ queryKey: [otcInvoicesBaseKey] });
    return result;
  };
}
